// JWT payload decoding (task 326.18, finding WEB-016).
//
// JWT segments are base64URL, not base64: '+' and '/' are replaced by '-' and '_', and the padding
// is dropped. Three call sites used bare `atob`, which throws on '-' and '_'. For ASCII JSON that
// only happens when a '?', '>', '~' or DEL lands on a triplet boundary — so it worked by accident,
// and a claim value such as a URL with a query string or a display name containing '~' would have
// made permission parsing return nothing and every menu disappear, with no error to trace.

/**
 * Decodes the payload of a JWT. Returns null for anything that is not a decodable JWT — callers
 * treat that as "no claims", never as a crash.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const segment = token.split('.')[1];
    if (!segment) return null;

    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    // atob yields one char per byte; the payload is UTF-8, so decode it as such rather than
    // assuming Latin-1 (a non-ASCII display name would otherwise come back mangled).
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const json = new TextDecoder().decode(bytes);

    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** `exp` as epoch milliseconds, or null when the token carries none. */
export function getTokenExpiry(token: string): number | null {
  const exp = decodeJwtPayload(token)?.['exp'];
  return typeof exp === 'number' ? exp * 1000 : null;
}
